import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  CalendarClock, Plus, Search, FileText, Users, BookOpen,
  Hash, UserSearch, Phone, User, X, CheckCircle, AlertTriangle,
  Loader2, Clock, Trash2, Eye, Edit3, XCircle, Briefcase, Contact,
  Repeat, CalendarDays, Timer,
} from "lucide-react";
import {
  AppButton, AppCard, AppCardHeader, AppCardTitle, AppCardContent,
  EmptyState, ConfirmModal,
} from "../ui";
import { Form, Spinner, Modal } from "react-bootstrap";
import apiClient from "../../utils/apiClient";
import { can } from "../../utils/access";
import moment from "moment";
import { varGroupsForAudience, isVarValidForAudience, dynamicVarLabel } from "./whatsappTemplateVars";
import "./scheduled-messages.scss";

/* ── Constants ──────────────────────────────────────────────────────────────── */
const AUDIENCE_TYPES = [
  { value: "all_students", label: "All Students", icon: Users, desc: "Every student in your institute" },
  { value: "contact_groups", label: "Contact Groups", icon: Contact, desc: "Your saved lists of phone numbers" },
  { value: "course", label: "By Course", icon: BookOpen, desc: "Students enrolled in a specific course" },
  { value: "batch", label: "By Batch", icon: Hash, desc: "Students in a specific batch" },
  { value: "specific_students", label: "Specific Students", icon: UserSearch, desc: "Select individual students" },
  { value: "external_numbers", label: "External Numbers", icon: Phone, desc: "Enter phone numbers manually" },
  { value: "trainers", label: "Trainers", icon: Briefcase, desc: "Institute trainers/instructors" },
];

const RECIPIENT_STATUS_OPTIONS = [
  { value: "active", label: "Active Only", icon: CheckCircle },
  { value: "inactive", label: "Inactive Only", icon: XCircle },
  { value: "all", label: "All (Active + Inactive)", icon: Users },
];

const STATUS_MAP = {
  scheduled: { label: "Scheduled", variant: "pending", color: "#f59e0b" },
  processing: { label: "Processing", variant: "pending", color: "#3b82f6" },
  sent: { label: "Sent", variant: "active", color: "#22c55e" },
  partially_sent: { label: "Partial", variant: "warning", color: "#f97316" },
  failed: { label: "Failed", variant: "destructive", color: "#ef4444" },
  cancelled: { label: "Cancelled", variant: "inactive", color: "#6b7280" },
};

/* ── Recurrence ─────────────────────────────────────────────────────────────
 *
 * The rule is described here but never *evaluated* here. Fire times come from
 * `POST /whatsapp/scheduled/preview-occurrences`, which runs the same engine the
 * cron fires from — a second implementation in the browser would eventually
 * show one set of dates and send on another.
 */
const FREQUENCY_OPTIONS = [
  { value: "none", label: "Once", icon: CalendarClock, desc: "Send a single time" },
  { value: "daily", label: "Daily", icon: Repeat, desc: "Every day, or every N days" },
  { value: "weekly", label: "Weekly", icon: CalendarDays, desc: "On chosen days of the week" },
  { value: "monthly", label: "Monthly", icon: CalendarDays, desc: "On a day of the month" },
];

const WEEKDAYS = [
  { value: 0, short: "S", label: "Sun" },
  { value: 1, short: "M", label: "Mon" },
  { value: 2, short: "T", label: "Tue" },
  { value: 3, short: "W", label: "Wed" },
  { value: 4, short: "T", label: "Thu" },
  { value: 5, short: "F", label: "Fri" },
  { value: 6, short: "S", label: "Sat" },
];

/** Lead times for "before class starts". Anything under 5 minutes is a message
 *  that lands after the student has already opened the class link. */
const CLASS_OFFSETS = [5, 10, 15, 30, 60];

const EMPTY_RECURRENCE = {
  frequency: "none",
  interval: 1,
  daysOfWeek: [],
  dayOfMonth: null,
  anchor: "fixed",
  offsetMinutes: 15,
  endMode: "never",
  endDate: "",
  maxOccurrences: 10,
};

/** Server shape → form state. */
function recurrenceFromMessage(msg) {
  const r = msg?.recurrence;
  if (!r || !r.frequency || r.frequency === "none") return { ...EMPTY_RECURRENCE };
  return {
    frequency: r.frequency,
    interval: r.interval || 1,
    daysOfWeek: r.daysOfWeek || [],
    dayOfMonth: r.dayOfMonth || null,
    anchor: r.anchor || "fixed",
    offsetMinutes: r.offsetMinutes || 15,
    endMode: r.endMode || "never",
    endDate: r.endDate ? moment(r.endDate).format("YYYY-MM-DD") : "",
    maxOccurrences: r.maxOccurrences || 10,
  };
}

/** Form state → the payload the API validates. */
function recurrenceToPayload(rec) {
  if (rec.frequency === "none") return { frequency: "none" };
  return {
    frequency: rec.frequency,
    interval: Number(rec.interval) || 1,
    daysOfWeek: rec.frequency === "weekly" ? rec.daysOfWeek : [],
    dayOfMonth: rec.frequency === "monthly" ? Number(rec.dayOfMonth) || null : null,
    anchor: rec.anchor,
    offsetMinutes: rec.anchor === "before_class" ? Number(rec.offsetMinutes) || 15 : 0,
    endMode: rec.endMode,
    // Date-only, read as the institute's local day on the server.
    endDate: rec.endMode === "on_date" && rec.endDate ? new Date(`${rec.endDate}T23:59:59`).toISOString() : null,
    maxOccurrences: rec.endMode === "after_count" ? Number(rec.maxOccurrences) || null : null,
  };
}

/** A recurring row that has not yet ended — the one state where "next send" is
 *  a real thing rather than a leftover date. */
const isLiveSeries = (msg) => (
  !!msg?.recurrence?.frequency
  && msg.recurrence.frequency !== "none"
  && !msg.recurrence.completedAt
);

/** <optgroup>s for the field catalog the chosen audience can actually resolve —
 *  grouped because a flat list of ~50 fields is unscannable.
 *
 *  ⚠️ The catalog is audience-dependent. A contact group carries only a phone,
 *  a name and a course, so offering `pending_fees` there would build a picker
 *  whose choices the backend strips (`varKeysFor`) and which would render as a
 *  hole in the message. Single source: ./whatsappTemplateVars, mirroring
 *  vc_be/utils/whatsappTemplateVars. */
const renderVarOptions = (audienceType) => varGroupsForAudience(audienceType).map(({ group, vars }) => (
  <optgroup key={group} label={group}>
    {vars.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
  </optgroup>
));

const Loader = () => (
  <div className="wa-loader"><Loader2 size={20} className="wa-spin" /> Loading…</div>
);

/** "2h 15m" — a large send runs for hours, so minutes alone stop being useful. */
function formatDuration(minutes) {
  if (!minutes || minutes < 1) return "a minute";
  if (minutes < 60) return `${minutes} minutes`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m ? `${h}h ${m}m` : `${h} hour${h !== 1 ? "s" : ""}`;
}

/* ── Helpers ────────────────────────────────────────────────────────────────── */
/**
 * Every param carries both a `key` and a `token`:
 *   key   — unique within its group; safe as a React key and a state key. Button
 *           params namespace it, because two buttons can each own a {{1}}.
 *   token — the raw text inside the braces, exactly as Meta sees it.
 *
 * Anything that reaches Meta — `parameter_name`, the paramConfig key, the
 * preview substitution — must read `token`. Keying off the namespaced `key`
 * sent a URL-button {{1}} as parameter_name="btn_0_1", which Meta has no way to
 * place: positional params must be sent without a parameter_name at all.
 */
function _extractParams(component) {
  if (!component?.text) return [];
  const params = [];
  const namedRegex = /\{\{([a-zA-Z_]\w*)\}\}/g;
  const positionalRegex = /\{\{(\d+)\}\}/g;
  let m;
  while ((m = namedRegex.exec(component.text)) !== null) {
    params.push({ key: m[1], token: m[1], label: m[1].replace(/_/g, ' '), example: component.example?.body_text?.[0]?.[params.length] || '' });
  }
  const posnMatches = [...component.text.matchAll(positionalRegex)];
  posnMatches.forEach(pm => {
    if (!params.find(p => p.key === pm[1])) {
      params.push({ key: pm[1], token: pm[1], label: `Variable ${pm[1]}`, example: component.example?.body_text?.[0]?.[Number(pm[1]) - 1] || '' });
    }
  });
  return params;
}

function _extractBtnParams(btcComponent) {
  if (!btcComponent?.buttons) return [];
  const params = [];
  btcComponent.buttons.forEach((btn, idx) => {
    if (btn.type === 'URL' && btn.url) {
      const match = btn.url.match(/\{\{(\d+)\}\}/);
      if (match) {
        params.push({ key: `btn_${idx}_${match[1]}`, token: match[1], label: `Button "${btn.text}" URL suffix`, buttonIndex: idx, example: btn.example?.[0] || '', baseUrl: btn.url.replace(/\/?\{\{\d+\}\}$/, '') });
      }
    }
  });
  return params;
}

function _extractTemplateVars(template) {
  if (!template?.components) return { header: [], body: [], button: [] };
  const hc = template.components.find(c => c.type === "HEADER");
  const bc = template.components.find(c => c.type === "BODY");
  const btc = template.components.find(c => c.type === "BUTTONS");
  return { header: _extractParams(hc), body: _extractParams(bc), button: _extractBtnParams(btc) };
}

function _buildComponents(vars, values) {
  const components = [];
  const mkParam = (p, prefix) => {
    const param = { type: "text", text: values[`${prefix}_${p.key}`] || "" };
    // Off `token`, not `key`: a namespaced button key would go out as a named
    // parameter, and Meta cannot place a named param on a positional slot.
    if (!/^\d+$/.test(p.token)) param.parameter_name = p.token;
    return param;
  };
  if (vars.header.length) components.push({ type: "header", parameters: vars.header.map(p => mkParam(p, "header")) });
  if (vars.body.length) components.push({ type: "body", parameters: vars.body.map(p => mkParam(p, "body")) });
  if (vars.button.length) {
    const byBtn = {};
    vars.button.forEach(p => { if (!byBtn[p.buttonIndex]) byBtn[p.buttonIndex] = []; byBtn[p.buttonIndex].push(p); });
    Object.entries(byBtn).forEach(([idx, params]) => {
      components.push({ type: "button", sub_type: "url", index: Number(idx), parameters: params.map(p => mkParam(p, "button")) });
    });
  }
  return components;
}

function _fillPreview(text, params, values, prefix) {
  if (!text) return text;
  let result = text;
  // Match on `token` (what is actually in the text) but look the value up by
  // `key` (what the state map is keyed on).
  params.forEach(p => { result = result.replace(`{{${p.token}}}`, values[`${prefix}_${p.key}`] || `{{${p.token}}}`); });
  return result;
}

/* ══════════════════════════════════════════════════════════════════════════════
   Scheduled Messages Tab (main export)
   ══════════════════════════════════════════════════════════════════════════════ */
const ScheduledMessagesTab = ({ showToast }) => {
  const canManage = can("whatsapp.manage");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingMessage, setEditingMessage] = useState(null);
  const [viewingMessage, setViewingMessage] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const limit = 10;

  // A ref, not state: the poll effect reads it without wanting to resubscribe
  // every time the list refreshes.
  const hasLiveRunRef = useRef(false);

  const fetchMessages = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    try {
      const params = { page, limit };
      if (statusFilter) params.status = statusFilter;
      const { data } = await apiClient.get("/whatsapp/scheduled", { params });
      const rows = data.messages || [];
      setMessages(rows);
      setTotal(data.total || 0);
      hasLiveRunRef.current = rows.some((m) => (
        m.runId && ["queued", "dispatching", "sending"].includes(m.runId.status)
      ));
    } catch {
      if (!quiet) showToast("Failed to load scheduled messages", "error");
    }
    if (!quiet) setLoading(false);
  }, [page, statusFilter, showToast]);

  useEffect(() => { fetchMessages(); }, [fetchMessages]);

  /**
   * Refresh in place while a schedule is mid-send.
   *
   * Only while something is live, and quietly — a 50k send takes hours, and a
   * tab left open on a finished list must not sit polling the API forever. 8s
   * because the worker moves ~50 messages a minute; anything faster redraws the
   * same numbers.
   */
  useEffect(() => {
    const id = setInterval(() => {
      if (hasLiveRunRef.current) fetchMessages({ quiet: true });
    }, 8000);
    return () => clearInterval(id);
  }, [fetchMessages]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await apiClient.delete(`/whatsapp/scheduled/${deleteTarget._id}`);
      showToast("Scheduled message cancelled");
      setDeleteTarget(null);
      fetchMessages();
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to cancel", "error");
    }
    setDeleting(false);
  };

  const handleEdit = (msg) => {
    setEditingMessage(msg);
    setShowForm(true);
  };

  const handleFormClose = (refresh) => {
    setShowForm(false);
    setEditingMessage(null);
    if (refresh) fetchMessages();
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="wa-scheduled">
      {/* Header */}
      <div className="wa-scheduled__header">
        <div className="wa-scheduled__header-text">
          <h3 className="wa-scheduled__title"><CalendarClock size={20} /> Scheduled Messages</h3>
          <p className="wa-scheduled__subtitle">
            Send once, or on a repeat — weekend holiday notices, fee reminders, and
            &ldquo;your class starts in 15 minutes&rdquo; before every class of a batch
          </p>
        </div>
        {canManage && (
          <AppButton onClick={() => { setEditingMessage(null); setShowForm(true); }}>
            <Plus size={14} /> Schedule Message
          </AppButton>
        )}
      </div>

      {/* Filters */}
      <div className="wa-scheduled__filters">
        <Form.Select
          size="sm"
          className="wa-scheduled__filter-select"
          value={statusFilter}
          onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
        >
          <option value="">All Statuses</option>
          <option value="scheduled">Scheduled</option>
          <option value="sent">Sent</option>
          <option value="failed">Failed</option>
          <option value="cancelled">Cancelled</option>
        </Form.Select>
        <span className="wa-scheduled__count">{total} message{total !== 1 ? "s" : ""}</span>
      </div>

      {/* List */}
      {loading ? <Loader /> : messages.length === 0 ? (
        <EmptyState
          icon={<CalendarClock size={40} />}
          title="No Scheduled Messages"
          description="Schedule your first WhatsApp message for events, holidays, or batch reminders."
        />
      ) : (
        <div className="wa-scheduled__list">
          {messages.map(msg => (
            <ScheduledMessageCard
              key={msg._id}
              message={msg}
              onView={() => setViewingMessage(msg)}
              onEdit={canManage ? () => handleEdit(msg) : undefined}
              onDelete={canManage ? () => setDeleteTarget(msg) : undefined}
            />
          ))}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="wa-scheduled__pagination">
              <AppButton
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage(p => p - 1)}
              >Previous</AppButton>
              <span className="wa-scheduled__page-info">Page {page} of {totalPages}</span>
              <AppButton
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage(p => p + 1)}
              >Next</AppButton>
            </div>
          )}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showForm && (
        <ScheduleMessageForm
          editingMessage={editingMessage}
          onClose={handleFormClose}
          showToast={showToast}
        />
      )}

      {/* View Detail Modal */}
      {viewingMessage && (
        <ScheduledMessageDetail
          message={viewingMessage}
          onClose={() => setViewingMessage(null)}
        />
      )}

      {/* Delete Confirmation */}
      <ConfirmModal
        show={!!deleteTarget}
        onHide={() => { if (!deleting) setDeleteTarget(null); }}
        onConfirm={handleDelete}
        title={isLiveSeries(deleteTarget) ? "Stop This Repeat" : "Cancel Scheduled Message"}
        variant="warning"
        confirmText={isLiveSeries(deleteTarget) ? "Stop Repeat" : "Cancel Message"}
        cancelText="Keep"
        loading={deleting}
        message={
          <div>
            <p>
              {isLiveSeries(deleteTarget)
                ? "Stop this repeating message? No further occurrences will be scheduled."
                : "Are you sure you want to cancel this scheduled message?"}
            </p>
            {deleteTarget && (
              <div className="wa-scheduled__delete-info">
                <strong>{deleteTarget.title}</strong>
                <span>
                  {isLiveSeries(deleteTarget) ? "Next send " : "Scheduled for "}
                  {moment(deleteTarget.scheduledAt).format("MMM DD, YYYY hh:mm A")}
                </span>
                {isLiveSeries(deleteTarget) && deleteTarget.recurrenceLabel && (
                  <span>{deleteTarget.recurrenceLabel}</span>
                )}
              </div>
            )}
            {isLiveSeries(deleteTarget) && (
              <div className="wa-alert wa-alert--info" style={{ marginTop: 8 }}>
                <AlertTriangle size={14} />
                A send that has already started keeps going — stop that one from the{" "}
                <strong>Broadcasts</strong> tab.
              </div>
            )}
          </div>
        }
      />
    </div>
  );
};

/* ── Scheduled Message Card ────────────────────────────────────────────────── */
const ScheduledMessageCard = ({ message, onView, onEdit, onDelete }) => {
  const statusInfo = STATUS_MAP[message.status] || STATUS_MAP.scheduled;
  const audienceInfo = AUDIENCE_TYPES.find(a => a.value === message.audienceType);
  const AudienceIcon = audienceInfo?.icon || Users;
  const isPast = new Date(message.scheduledAt) < new Date();
  const canEdit = message.status === "scheduled";
  // A live series is always cancellable — stopping the repeat is a different
  // action from cancelling a send that has already started.
  const canDelete = !["sent", "processing"].includes(message.status) || isLiveSeries(message);
  // Populated only once the schedule has fired.
  const run = message.runId && typeof message.runId === "object" ? message.runId : null;

  const live = isLiveSeries(message);
  const rec = message.recurrence;

  return (
    <div className={`wa-sched-card ${message.status === 'cancelled' ? 'wa-sched-card--dimmed' : ''}`}>
      <div className="wa-sched-card__top">
        <div className="wa-sched-card__title-row">
          <h4 className="wa-sched-card__title">{message.title}</h4>
          {/* A repeat is the first thing to know about a row — an operator
              reading "Sent" on a weekly reminder needs to see that it is still
              armed, not finished. */}
          {live && (
            <span className="wa-sched-card__repeat" title={message.recurrenceLabel}>
              <Repeat size={11} /> Repeats
            </span>
          )}
          <span className={`wa-sched-card__status wa-sched-card__status--${message.status}`}>
            {statusInfo.label}
          </span>
        </div>
        {message.description && (
          <p className="wa-sched-card__desc">{message.description}</p>
        )}
      </div>

      <div className="wa-sched-card__meta">
        <div className="wa-sched-card__meta-item">
          <Clock size={13} />
          <span>
            {live ? "Next " : ""}
            {moment(message.scheduledAt).format("MMM DD, YYYY · hh:mm A")}
          </span>
          {(message.status === 'scheduled' || live) && (
            <span className="wa-sched-card__relative-time">
              ({isPast && !live ? "overdue" : moment(message.scheduledAt).fromNow()})
            </span>
          )}
        </div>
        {rec && rec.frequency && rec.frequency !== "none" && (
          <div className="wa-sched-card__meta-item">
            <Repeat size={13} />
            <span>{message.recurrenceLabel}</span>
            {rec.occurrenceCount > 0 && (
              <span className="wa-sched-card__meta-dim">
                — {rec.occurrenceCount} sent{rec.skippedCount > 0 ? `, ${rec.skippedCount} skipped` : ""}
              </span>
            )}
            {rec.completedAt && <span className="wa-sched-card__meta-dim">— ended</span>}
          </div>
        )}
        <div className="wa-sched-card__meta-item">
          <AudienceIcon size={13} />
          <span>{audienceInfo?.label || message.audienceType}</span>
          {message.courseId?.name && <span className="wa-sched-card__meta-dim">— {message.courseId.name}</span>}
          {message.batchId?.name && <span className="wa-sched-card__meta-dim">— {message.batchId.name}</span>}
          {(message.contactGroupIds || []).map(g => (
            <span key={g._id} className="wa-sched-card__meta-dim">— {g.name}</span>
          ))}
        </div>
        <div className="wa-sched-card__meta-item">
          <FileText size={13} />
          <span>{message.templateName}</span>
        </div>
        <div className="wa-sched-card__meta-item">
          <Users size={13} />
          <span>{(message.recipientCount || 0).toLocaleString()} recipient{message.recipientCount !== 1 ? "s" : ""}</span>
        </div>
      </div>

      {/* Live outcome, read off the run rather than the schedule's mirrored
          `sentCount` — that field lags by a finaliser tick and, on records
          created before runs existed, counts messages *queued* rather than
          sent. A 50k send is visibly still moving here for hours. */}
      {run && <RunProgressStrip run={run} />}

      <div className="wa-sched-card__actions">
        <button className="wa-sched-card__action-btn" onClick={onView} title="View details">
          <Eye size={14} /> View
        </button>
        {canEdit && onEdit && (
          <button className="wa-sched-card__action-btn wa-sched-card__action-btn--edit" onClick={onEdit} title="Edit">
            <Edit3 size={14} /> Edit
          </button>
        )}
        {canDelete && onDelete && (
          <button
            className="wa-sched-card__action-btn wa-sched-card__action-btn--delete"
            onClick={onDelete}
            title={live ? "Stop this repeat" : "Cancel"}
          >
            <Trash2 size={14} /> {live ? "Stop repeat" : "Cancel"}
          </button>
        )}
      </div>
    </div>
  );
};

/* ── Run progress strip ─────────────────────────────────────────────────────
 *
 * The compact form of BroadcastRunsTab's card. Progress is measured as settled
 * outcomes over total recipients, NOT over `enqueuedCount`: the dispatcher runs
 * ahead of the sender by design, so a bar tracking enqueue jumps to full within
 * a minute of a 16-hour send starting and then stops moving.
 */
const RunProgressStrip = ({ run }) => {
  const total = run.totalRecipients || run.enqueuedCount || 0;
  const settled = (run.sentCount || 0) + (run.failedCount || 0) + (run.skippedCount || 0);
  const isLive = ["queued", "dispatching", "sending"].includes(run.status);
  const w = (n) => (total > 0 ? `${Math.min(100, ((n || 0) / total) * 100)}%` : "0%");

  return (
    <div className="wa-sched-card__run">
      <div className="wa-sched-card__run-bar">
        <div className="wa-sched-card__run-seg wa-sched-card__run-seg--delivered" style={{ width: w(run.deliveredCount) }} />
        <div className="wa-sched-card__run-seg wa-sched-card__run-seg--sent" style={{ width: w(Math.max(0, (run.sentCount || 0) - (run.deliveredCount || 0))) }} />
        <div className="wa-sched-card__run-seg wa-sched-card__run-seg--failed" style={{ width: w(run.failedCount) }} />
        <div className="wa-sched-card__run-seg wa-sched-card__run-seg--skipped" style={{ width: w(run.skippedCount) }} />
      </div>
      <div className="wa-sched-card__run-text">
        {isLive && <span className="wa-sched-card__run-live">Sending</span>}
        <strong>{settled.toLocaleString()}</strong> of {total.toLocaleString()} processed
        {run.deliveredCount > 0 && <> · {run.deliveredCount.toLocaleString()} delivered</>}
        {run.failedCount > 0 && <> · <span className="wa-sched-card__run-bad">{run.failedCount.toLocaleString()} failed</span></>}
        {run.skippedCount > 0 && <> · <span className="wa-sched-card__run-warn">{run.skippedCount.toLocaleString()} skipped</span></>}
      </div>
    </div>
  );
};

/* ── Scheduled Message Detail Modal ────────────────────────────────────────── */
const ScheduledMessageDetail = ({ message, onClose }) => {
  const statusInfo = STATUS_MAP[message.status] || STATUS_MAP.scheduled;
  const audienceInfo = AUDIENCE_TYPES.find(a => a.value === message.audienceType);
  const rec = message.recurrence;
  const repeats = !!rec?.frequency && rec.frequency !== "none";
  const live = isLiveSeries(message);

  // Past occurrences are the broadcast runs this schedule produced — read from
  // the server rather than kept as a second copy on the schedule, so the numbers
  // here and on the Broadcasts tab can never disagree.
  const [occurrences, setOccurrences] = useState(null);
  useEffect(() => {
    if (!repeats) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await apiClient.get(`/whatsapp/scheduled/${message._id}/occurrences`);
        if (!cancelled) setOccurrences(data.occurrences || []);
      } catch { if (!cancelled) setOccurrences([]); }
    })();
    return () => { cancelled = true; };
  }, [message._id, repeats]);

  return (
    <Modal show centered size="lg" onHide={onClose} className="wa-scheduled-modal">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "1rem", fontWeight: 600 }}>
          <CalendarClock size={18} /> Scheduled Message Details
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="wa-sched-detail">
          <div className="wa-sched-detail__row">
            <span className="wa-sched-detail__label">Title</span>
            <span>{message.title}</span>
          </div>
          {message.description && (
            <div className="wa-sched-detail__row">
              <span className="wa-sched-detail__label">Description</span>
              <span>{message.description}</span>
            </div>
          )}
          <div className="wa-sched-detail__row">
            <span className="wa-sched-detail__label">Status</span>
            <span className={`wa-sched-card__status wa-sched-card__status--${message.status}`}>{statusInfo.label}</span>
          </div>
          <div className="wa-sched-detail__row">
            <span className="wa-sched-detail__label">{live ? "Next Send" : "Scheduled At"}</span>
            <span>{moment(message.scheduledAt).format("MMM DD, YYYY · hh:mm A")}</span>
          </div>
          {repeats && (
            <>
              <div className="wa-sched-detail__row">
                <span className="wa-sched-detail__label">Repeats</span>
                <span>
                  {message.recurrenceLabel}
                  {rec.completedAt && <span className="wa-sched-card__meta-dim"> — ended {moment(rec.completedAt).format("DD MMM YYYY")}</span>}
                </span>
              </div>
              <div className="wa-sched-detail__row">
                <span className="wa-sched-detail__label">Sends So Far</span>
                <span>
                  {rec.occurrenceCount || 0}
                  {rec.skippedCount > 0 && ` · ${rec.skippedCount} skipped`}
                  {rec.lastOccurrenceAt && ` · last ${moment(rec.lastOccurrenceAt).format("DD MMM, hh:mm A")}`}
                </span>
              </div>
            </>
          )}
          <div className="wa-sched-detail__row">
            <span className="wa-sched-detail__label">Template</span>
            <span>{message.templateName} ({message.languageCode})</span>
          </div>
          <div className="wa-sched-detail__row">
            <span className="wa-sched-detail__label">Audience</span>
            <span>{audienceInfo?.label || message.audienceType}</span>
          </div>
          <div className="wa-sched-detail__row">
            <span className="wa-sched-detail__label">Recipient Filter</span>
            <span style={{ textTransform: "capitalize" }}>{message.recipientStatus || "active"}</span>
          </div>
          {message.courseId?.name && (
            <div className="wa-sched-detail__row">
              <span className="wa-sched-detail__label">Course</span>
              <span>{message.courseId.name}{message.courseId.title ? ` (${message.courseId.title})` : ""}</span>
            </div>
          )}
          {message.batchId?.name && (
            <div className="wa-sched-detail__row">
              <span className="wa-sched-detail__label">Batch</span>
              <span>{message.batchId.name}</span>
            </div>
          )}
          <div className="wa-sched-detail__row">
            <span className="wa-sched-detail__label">Recipients</span>
            <span>{message.recipientCount}</span>
          </div>
          {message.sentCount > 0 && (
            <div className="wa-sched-detail__row">
              <span className="wa-sched-detail__label">Sent</span>
              <span>{message.sentCount}</span>
            </div>
          )}
          {message.executedAt && (
            <div className="wa-sched-detail__row">
              <span className="wa-sched-detail__label">Executed At</span>
              <span>{moment(message.executedAt).format("MMM DD, YYYY · hh:mm A")}</span>
            </div>
          )}
          {message.executionError && (
            <div className="wa-alert wa-alert--warning" style={{ marginTop: 8 }}>
              <AlertTriangle size={14} /> {message.executionError}
            </div>
          )}
          {message.createdBy?.name && (
            <div className="wa-sched-detail__row">
              <span className="wa-sched-detail__label">Created By</span>
              <span>{message.createdBy.name}</span>
            </div>
          )}
          <div className="wa-sched-detail__row">
            <span className="wa-sched-detail__label">Created</span>
            <span>{moment(message.createdAt).format("MMM DD, YYYY · hh:mm A")}</span>
          </div>
        </div>

        {repeats && (
          <div className="wa-sched-occ wa-sched-occ--history">
            <div className="wa-sched-occ__head">
              <Repeat size={14} /> <strong>Send history</strong>
            </div>
            {occurrences === null ? (
              <div className="wa-sched-occ__empty"><Loader2 size={14} className="wa-spin" /> Loading…</div>
            ) : occurrences.length === 0 ? (
              <div className="wa-sched-occ__empty">Nothing has gone out yet.</div>
            ) : (
              <ol className="wa-sched-occ__list">
                {occurrences.map((run) => (
                  <li key={run._id}>
                    <span className="wa-sched-occ__when">
                      {moment(run.createdAt).format("ddd, DD MMM · hh:mm A")}
                    </span>
                    <span className="wa-sched-occ__rel">
                      {(run.sentCount || 0).toLocaleString()} sent
                      {run.failedCount > 0 && ` · ${run.failedCount.toLocaleString()} failed`}
                      {run.skippedCount > 0 && ` · ${run.skippedCount.toLocaleString()} skipped`}
                      {run.error && ` · ${run.error}`}
                    </span>
                  </li>
                ))}
              </ol>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <AppButton variant="outline" onClick={onClose}>Close</AppButton>
      </Modal.Footer>
    </Modal>
  );
};

/* ══════════════════════════════════════════════════════════════════════════════
   Schedule Message Form (Create / Edit) — Full-page Modal
   ══════════════════════════════════════════════════════════════════════════════ */
const ScheduleMessageForm = ({ editingMessage, onClose, showToast }) => {
  // ── Data loading ──
  const [templates, setTemplates] = useState([]);
  const [courses, setCourses] = useState([]);
  const [batches, setBatches] = useState([]);
  const [contactGroups, setContactGroups] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);

  // ── Form fields ──
  const [title, setTitle] = useState(editingMessage?.title || "");
  const [description, setDescription] = useState(editingMessage?.description || "");
  const [scheduledDate, setScheduledDate] = useState(
    editingMessage?.scheduledAt ? moment(editingMessage.scheduledAt).format("YYYY-MM-DD") : ""
  );
  const [scheduledTime, setScheduledTime] = useState(
    editingMessage?.scheduledAt ? moment(editingMessage.scheduledAt).format("HH:mm") : ""
  );

  // ── Repeat ──
  const [recurrence, setRecurrence] = useState(() => recurrenceFromMessage(editingMessage));
  const [preview, setPreview] = useState(null);   // { occurrences[], description, warning }
  const [previewLoading, setPreviewLoading] = useState(false);
  const previewTimer = useRef(null);
  const setRec = (patch) => setRecurrence((r) => ({ ...r, ...patch }));
  const isRepeating = recurrence.frequency !== "none";
  const classAnchored = isRepeating && recurrence.anchor === "before_class";

  // ── Audience ──
  const [audienceType, setAudienceType] = useState(editingMessage?.audienceType || "all_students");
  const [recipientStatus, setRecipientStatus] = useState(editingMessage?.recipientStatus || "active");
  const [courseId, setCourseId] = useState(editingMessage?.courseId?._id || editingMessage?.courseId || "");
  const [batchId, setBatchId] = useState(editingMessage?.batchId?._id || editingMessage?.batchId || "");
  const [selectedGroupIds, setSelectedGroupIds] = useState(
    (editingMessage?.contactGroupIds || []).map((g) => g._id || g),
  );
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [studentQuery, setStudentQuery] = useState("");
  const [studentResults, setStudentResults] = useState([]);
  const [studentSearching, setStudentSearching] = useState(false);
  const searchTimer = useRef(null);

  // ── External numbers ──
  const [externalNumbersText, setExternalNumbersText] = useState(
    editingMessage?.externalNumbers?.join("\n") || ""
  );
  const parsedExternalNumbers = useMemo(() => {
    if (!externalNumbersText.trim()) return [];
    return externalNumbersText
      .split(/[\n,;]+/)
      .map(n => n.trim().replace(/[\s()+\u002D]/g, ''))
      .filter(n => n.length >= 10);
  }, [externalNumbersText]);

  // ── Template ──
  const [templateName, setTemplateName] = useState(editingMessage?.templateName || "");
  const [paramValues, setParamValues] = useState({});
  const [paramModes, setParamModes] = useState({});

  // ── Recipient count + send capacity ──
  const [recipientCount, setRecipientCount] = useState(null);
  // What the institute's daily cap and the worker's throughput mean for this
  // audience. Shown before scheduling, because a 50k send against the default
  // 1,000/day cap delivers 1,000 and skips the rest — and that must not be a
  // surprise discovered from tomorrow's completion notice.
  const [capacity, setCapacity] = useState(null);
  const [countLoading, setCountLoading] = useState(false);
  const countTimer = useRef(null);

  // ── Submission ──
  const [saving, setSaving] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // ── Derived ──
  const selectedTemplate = useMemo(() => templates.find(t => t.name === templateName) || null, [templates, templateName]);
  const templateVars = useMemo(() => selectedTemplate ? _extractTemplateVars(selectedTemplate) : { header: [], body: [], button: [] }, [selectedTemplate]);
  const totalParams = templateVars.header.length + templateVars.body.length + templateVars.button.length;

  const filteredBatches = useMemo(() => {
    if (audienceType === "batch" && courseId) {
      return batches.filter(b => (b.course?._id || b.course) === courseId);
    }
    return batches;
  }, [batches, audienceType, courseId]);

  const allParamsFilled = totalParams === 0 || [
    ...templateVars.header.map(p => `header_${p.key}`),
    ...templateVars.body.map(p => `body_${p.key}`),
    ...templateVars.button.map(p => `button_${p.key}`),
  ].every(k => {
    if (paramModes[k] === 'dynamic' && paramValues[k]) return true;
    return paramModes[k] !== 'dynamic' && paramValues[k]?.trim();
  });

  const audienceReady = (
    (audienceType === "all_students") ||
    (audienceType === "course" && courseId) ||
    (audienceType === "batch" && batchId) ||
    (audienceType === "specific_students" && selectedStudents.length > 0) ||
    (audienceType === "external_numbers" && parsedExternalNumbers.length > 0) ||
    (audienceType === "contact_groups" && selectedGroupIds.length > 0) ||
    (audienceType === "trainers")
  );

  /**
   * Variables that no longer resolve for the chosen audience.
   *
   * Reachable by switching a template's audience from students to a contact
   * group after mapping `student_name`. The backend strips these silently, so
   * without this the operator schedules a message that will ship with a gap
   * where the name should be and finds out from a customer.
   */
  const staleVars = useMemo(() => {
    const keys = [
      ...templateVars.header.map((p) => `header_${p.key}`),
      ...templateVars.body.map((p) => `body_${p.key}`),
      ...templateVars.button.map((p) => `button_${p.key}`),
    ];
    return keys
      .filter((k) => paramModes[k] === "dynamic" && paramValues[k])
      .filter((k) => !isVarValidForAudience(paramValues[k], audienceType))
      .map((k) => paramValues[k]);
  }, [templateVars, paramModes, paramValues, audienceType]);

  /** The repeat rule is internally complete — a weekly rule with no days picked
   *  would be saved and then never fire, so it is blocked here as well as on the
   *  server. */
  const recurrenceReady = (
    !isRepeating
    || (
      (recurrence.frequency !== "weekly" || recurrence.daysOfWeek.length > 0)
      && (recurrence.frequency !== "monthly" || !!recurrence.dayOfMonth)
      && (recurrence.endMode !== "on_date" || !!recurrence.endDate)
      && (recurrence.endMode !== "after_count" || Number(recurrence.maxOccurrences) >= 1)
      && (!classAnchored || (audienceType === "batch" && !!batchId))
    )
  );

  // A class-anchored repeat takes its date and time from the batch's own
  // timetable, so the date/time inputs are not asked for and not required.
  const scheduleReady = classAnchored
    ? !!title.trim()
    : !!title.trim() && !!scheduledDate && !!scheduledTime
      && new Date(`${scheduledDate}T${scheduledTime}`) > new Date();

  const canSubmit = scheduleReady && recurrenceReady && templateName
    && allParamsFilled && audienceReady && staleVars.length === 0
    // A repeat whose preview came back empty has nothing to send — saving it
    // would put a row in the list that looks armed and never fires.
    && !(isRepeating && preview && preview.occurrences.length === 0);

  // ── Load initial data ──
  useEffect(() => {
    (async () => {
      try {
        const [tplRes, optRes, grpRes] = await Promise.all([
          apiClient.get("/whatsapp/templates"),
          apiClient.get("/whatsapp/broadcast/options"),
          // Fail-soft on its own: a schedule against students must still be
          // creatable if the contacts endpoint is unavailable.
          apiClient.get("/whatsapp/contact-groups").catch(() => ({ data: { groups: [] } })),
        ]);
        setTemplates((tplRes.data.templates || []).filter(t => t.status === "APPROVED"));
        setCourses(optRes.data.courses || []);
        setBatches(optRes.data.batches || []);
        setContactGroups(grpRes.data.groups || []);
      } catch { /* ignore */ }
      setInitialLoading(false);
    })();
  }, []);

  // ── Restore paramConfig when editing ──
  useEffect(() => {
    if (editingMessage?.paramConfig && selectedTemplate) {
      const pc = editingMessage.paramConfig;
      const modes = {};
      const vals = {};
      const restore = (entries, prefix) => {
        (entries || []).forEach(e => {
          const k = `${prefix}_${e.key}`;
          modes[k] = e.type || "static";
          vals[k] = e.type === "dynamic" ? (e.variable || "") : (e.value || "");
        });
      };
      restore(pc.header, "header");
      restore(pc.body, "body");
      restore(pc.button, "button");
      setParamModes(modes);
      setParamValues(vals);
    }
  }, [editingMessage, selectedTemplate]);

  // ── Reset params when template changes (only for new messages) ──
  useEffect(() => {
    if (!editingMessage) { setParamValues({}); setParamModes({}); }
  }, [templateName, editingMessage]);

  // ── Fetch recipient count ──
  useEffect(() => {
    if (countTimer.current) clearTimeout(countTimer.current);
    setRecipientCount(null);
    setCapacity(null);
    if (!audienceReady) return;

    countTimer.current = setTimeout(async () => {
      setCountLoading(true);
      try {
        const body = { audienceType, recipientStatus };
        if (audienceType === "course") body.courseId = courseId;
        if (audienceType === "batch") body.batchId = batchId;
        if (audienceType === "specific_students") body.studentIds = selectedStudents.map(s => s._id);
        if (audienceType === "contact_groups") body.contactGroupIds = selectedGroupIds;
        // External numbers go to the server too, rather than being counted
        // locally: the server normalises to E.164 and dedupes, so "9876543210"
        // and "+91 98765 43210" are one recipient. Counting them here as two
        // would show a total the send never matches.
        if (audienceType === "external_numbers") body.externalNumbers = parsedExternalNumbers;
        const { data } = await apiClient.post("/whatsapp/scheduled/estimate-recipients", body);
        setRecipientCount(data.count);
        setCapacity(data.capacity || null);
      } catch { setRecipientCount(null); setCapacity(null); }
      setCountLoading(false);
    }, 400);

    return () => { if (countTimer.current) clearTimeout(countTimer.current); };
  }, [audienceType, courseId, batchId, selectedStudents, audienceReady, recipientStatus, parsedExternalNumbers, selectedGroupIds]);

  /**
   * Ask the server when this rule actually fires.
   *
   * Debounced and fired for one-time schedules too, so the panel below the
   * repeat controls always shows real dates. "Every 2nd Tuesday, 15 minutes
   * before class, minus holidays" is not something anyone can verify by reading
   * the form back — seeing the next five dates is the check.
   */
  useEffect(() => {
    if (previewTimer.current) clearTimeout(previewTimer.current);
    if (!isRepeating) { setPreview(null); return undefined; }
    if (!recurrenceReady) { setPreview(null); return undefined; }

    previewTimer.current = setTimeout(async () => {
      setPreviewLoading(true);
      try {
        const { data } = await apiClient.post("/whatsapp/scheduled/preview-occurrences", {
          recurrence: recurrenceToPayload(recurrence),
          audienceType,
          batchId: batchId || undefined,
          scheduledAt: scheduledDate && scheduledTime
            ? new Date(`${scheduledDate}T${scheduledTime}`).toISOString()
            : undefined,
          count: 5,
        });
        setPreview({
          occurrences: data.occurrences || [],
          description: data.description || "",
          warning: data.warning || null,
        });
      } catch (err) {
        setPreview({
          occurrences: [],
          description: "",
          warning: err?.response?.data?.message || "Could not work out when this repeats.",
        });
      }
      setPreviewLoading(false);
    }, 400);

    return () => { if (previewTimer.current) clearTimeout(previewTimer.current); };
  }, [recurrence, recurrenceReady, isRepeating, audienceType, batchId, scheduledDate, scheduledTime]);

  // "Before class starts" reads the batch's own start time, so it cannot
  // survive a move to any other audience. Reset rather than leave a rule the
  // server would reject on save.
  useEffect(() => {
    if (audienceType !== "batch" && recurrence.anchor === "before_class") {
      setRec({ anchor: "fixed" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audienceType]);

  // ── Student search ──
  const handleStudentSearch = useCallback((value) => {
    setStudentQuery(value);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (value.trim().length < 2) { setStudentResults([]); setStudentSearching(false); return; }
    setStudentSearching(true);
    searchTimer.current = setTimeout(async () => {
      try {
        const { data } = await apiClient.get(`/whatsapp/students/search?q=${encodeURIComponent(value.trim())}`);
        setStudentResults(data.students || []);
      } catch { setStudentResults([]); }
      setStudentSearching(false);
    }, 350);
  }, []);

  const addStudent = (s) => {
    if (!selectedStudents.find(x => x._id === s._id)) {
      setSelectedStudents(prev => [...prev, s]);
    }
    setStudentQuery("");
    setStudentResults([]);
  };

  const removeStudent = (id) => {
    setSelectedStudents(prev => prev.filter(s => s._id !== id));
  };

  // ── Submit ──
  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      const payload = {
        title: title.trim(),
        description: description.trim(),
        templateName,
        languageCode: selectedTemplate?.language || "en",
        audienceType,
        recipientStatus,
        recurrence: recurrenceToPayload(recurrence),
      };
      // Omitted entirely for a class-anchored repeat: the server resolves the
      // first send from the batch's timetable, and sending a date here would
      // just be a value it has to ignore.
      if (!classAnchored) {
        payload.scheduledAt = new Date(`${scheduledDate}T${scheduledTime}`).toISOString();
      }

      // Build paramConfig
      if (totalParams > 0) {
        const buildEntries = (params, prefix) => params.map(p => {
          const k = `${prefix}_${p.key}`;
          const mode = paramModes[k] || "static";
          return {
            // `token`, not `key` — the backend decides positional vs named from
            // this, and a namespaced button key reads as a named parameter.
            key: p.token,
            type: mode,
            variable: mode === "dynamic" ? (paramValues[k] || "") : "",
            value: mode === "static" ? (paramValues[k] || "") : "",
            ...(p.buttonIndex !== undefined ? { buttonIndex: p.buttonIndex } : {}),
          };
        });
        payload.paramConfig = {
          header: buildEntries(templateVars.header, "header"),
          body: buildEntries(templateVars.body, "body"),
          button: buildEntries(templateVars.button, "button"),
        };
        payload.components = _buildComponents(templateVars, paramValues);
      }

      if (audienceType === "course") payload.courseId = courseId;
      if (audienceType === "batch") payload.batchId = batchId;
      if (audienceType === "specific_students") payload.studentIds = selectedStudents.map(s => s._id);
      if (audienceType === "external_numbers") payload.externalNumbers = parsedExternalNumbers;
      if (audienceType === "contact_groups") payload.contactGroupIds = selectedGroupIds;

      if (editingMessage) {
        await apiClient.put(`/whatsapp/scheduled/${editingMessage._id}`, payload);
        showToast("Scheduled message updated");
      } else {
        await apiClient.post("/whatsapp/scheduled", payload);
        showToast("Message scheduled successfully");
      }
      setShowConfirmModal(false);
      onClose(true);
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to save", "error");
    }
    setSaving(false);
  };

  if (initialLoading) return (
    <Modal show centered size="xl" onHide={() => onClose(false)} className="wa-scheduled-modal">
      <Modal.Body><Loader /></Modal.Body>
    </Modal>
  );

  return (
    <>
      <Modal show centered size="xl" onHide={() => { if (!saving) onClose(false); }} className="wa-scheduled-modal">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "1rem", fontWeight: 600 }}>
            <CalendarClock size={18} /> {editingMessage ? "Edit Scheduled Message" : "Schedule New Message"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="wa-sched-form__body">
          <div className="wa-sched-form">
            {/* ── Step 1: Message Info & Schedule ── */}
            <AppCard>
              <AppCardHeader>
                <AppCardTitle><CalendarClock size={18} /> Step 1 — Message Info &amp; Schedule</AppCardTitle>
                <p className="settings-card-desc">Give your message a name and set when it should be sent</p>
              </AppCardHeader>
              <AppCardContent>
                <div className="wa-sched-form__grid">
                  <Form.Group>
                    <Form.Label>Title <span className="wa-required">*</span></Form.Label>
                    <Form.Control
                      value={title}
                      onChange={e => setTitle(e.target.value)}
                      placeholder="e.g., Diwali Greeting, New Batch Announcement..."
                      maxLength={200}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Description <span className="wa-optional">(optional)</span></Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={description}
                      onChange={e => setDescription(e.target.value)}
                      placeholder="Internal notes about this message..."
                      maxLength={500}
                    />
                  </Form.Group>
                </div>
                {/* ── Repeat ── */}
                <RepeatPicker
                  recurrence={recurrence}
                  setRec={setRec}
                  audienceType={audienceType}
                  batchName={batches.find((b) => b._id === batchId)?.name}
                />

                {!classAnchored && (
                  <>
                    <div className="wa-sched-form__datetime">
                      <Form.Group className="wa-sched-form__date-group">
                        <Form.Label>
                          {isRepeating ? "First send date" : "Date"} <span className="wa-required">*</span>
                        </Form.Label>
                        <Form.Control
                          type="date"
                          value={scheduledDate}
                          onChange={e => setScheduledDate(e.target.value)}
                          min={moment().format("YYYY-MM-DD")}
                        />
                      </Form.Group>
                      <Form.Group className="wa-sched-form__time-group">
                        <Form.Label>Time <span className="wa-required">*</span></Form.Label>
                        <Form.Control
                          type="time"
                          value={scheduledTime}
                          onChange={e => setScheduledTime(e.target.value)}
                        />
                      </Form.Group>
                    </div>
                    {scheduledDate && scheduledTime && !isRepeating && (
                      <div className="wa-sched-form__schedule-preview">
                        <Clock size={14} />
                        <span>
                          Will be sent on <strong>{moment(`${scheduledDate}T${scheduledTime}`).format("dddd, MMMM DD, YYYY")} at {moment(`${scheduledDate}T${scheduledTime}`).format("hh:mm A")}</strong>
                          {' '}({moment(`${scheduledDate}T${scheduledTime}`).fromNow()})
                        </span>
                      </div>
                    )}
                  </>
                )}

                {/* Real dates from the same engine the cron fires from — the
                    only honest way to check a rule before saving it. */}
                {isRepeating && (
                  <OccurrencePreview
                    preview={preview}
                    loading={previewLoading}
                    ready={recurrenceReady}
                    classAnchored={classAnchored}
                  />
                )}
              </AppCardContent>
            </AppCard>

            {/* ── Step 2: Select Audience ── */}
            <AppCard>
              <AppCardHeader>
                <AppCardTitle><Users size={18} /> Step 2 — Select Audience</AppCardTitle>
                <p className="settings-card-desc">Choose who will receive this message</p>
              </AppCardHeader>
              <AppCardContent>
                <div className="wa-bc-audience-grid">
                  {AUDIENCE_TYPES.map(a => {
                    const Icon = a.icon;
                    return (
                      <div
                        key={a.value}
                        className={`wa-bc-audience-card ${audienceType === a.value ? "wa-bc-audience-card--active" : ""}`}
                        onClick={() => {
                          setAudienceType(a.value);
                          setCourseId(""); setBatchId(""); setSelectedStudents([]);
                          setExternalNumbersText(""); setSelectedGroupIds([]);
                        }}
                        role="button"
                        tabIndex={0}
                        onKeyDown={e => e.key === "Enter" && setAudienceType(a.value)}
                      >
                        <div className="wa-bc-audience-card__icon"><Icon size={20} /></div>
                        <div className="wa-bc-audience-card__label">{a.label}</div>
                        <div className="wa-bc-audience-card__desc">{a.desc}</div>
                      </div>
                    );
                  })}
                </div>

                {/* Contact Group multi-select */}
                {audienceType === "contact_groups" && (
                  <div className="wa-bc-filter-section">
                    <Form.Label>Select one or more groups</Form.Label>
                    {contactGroups.length === 0 ? (
                      <div className="wa-alert wa-alert--info">
                        <AlertTriangle size={14} />
                        You have no contact groups yet. Create one in the <strong>Contacts</strong> tab
                        and import your numbers there once — then pick it here every time.
                      </div>
                    ) : (
                      <>
                        <div className="wa-sched-form__group-list">
                          {contactGroups.map((g) => {
                            const checked = selectedGroupIds.includes(g._id);
                            return (
                              <label
                                key={g._id}
                                className={`wa-sched-form__group ${checked ? "is-checked" : ""}`}
                                style={{ "--cg-color": g.color || "#25D366" }}
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => setSelectedGroupIds((prev) => (
                                    checked ? prev.filter((id) => id !== g._id) : [...prev, g._id]
                                  ))}
                                />
                                <span className="wa-sched-form__group-dot" />
                                <span className="wa-sched-form__group-name">{g.name}</span>
                                <span className="wa-sched-form__group-count">
                                  {(g.activeCount || 0).toLocaleString()}
                                </span>
                              </label>
                            );
                          })}
                        </div>
                        {selectedGroupIds.length > 1 && (
                          <div className="wa-form-hint" style={{ marginTop: 6 }}>
                            A number that appears in more than one of the selected groups is
                            messaged <strong>once</strong>.
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}

                {/* Recipient Status Filter — meaningless for audiences that are
                    not students: a contact has its own active/unsubscribed
                    state, and unsubscribed contacts are always excluded. */}
                {audienceType !== "external_numbers" && audienceType !== "contact_groups" && (
                  <div className="wa-sched-form__status-filter">
                    <Form.Label className="wa-sched-form__filter-label">Send to:</Form.Label>
                    <div className="wa-sched-form__status-btns">
                      {RECIPIENT_STATUS_OPTIONS.map(opt => {
                        const Icon = opt.icon;
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            className={`wa-mode-btn ${recipientStatus === opt.value ? "wa-mode-btn--active" : ""}`}
                            onClick={() => setRecipientStatus(opt.value)}
                          >
                            <Icon size={13} /> {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Course Selector */}
                {audienceType === "course" && (
                  <div className="wa-bc-filter-section">
                    <Form.Group>
                      <Form.Label>Select Course</Form.Label>
                      <Form.Select value={courseId} onChange={e => setCourseId(e.target.value)}>
                        <option value="">Choose a course...</option>
                        {courses.map(c => <option key={c._id} value={c._id}>{c.name}{c.title ? ` (${c.title})` : ""}</option>)}
                      </Form.Select>
                    </Form.Group>
                  </div>
                )}

                {/* Batch Selector */}
                {audienceType === "batch" && (
                  <div className="wa-bc-filter-section">
                    <Form.Group className="mb-2">
                      <Form.Label>Filter by Course (optional)</Form.Label>
                      <Form.Select value={courseId} onChange={e => { setCourseId(e.target.value); setBatchId(""); }}>
                        <option value="">All Courses</option>
                        {courses.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                      </Form.Select>
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>Select Batch</Form.Label>
                      <Form.Select value={batchId} onChange={e => setBatchId(e.target.value)}>
                        <option value="">Choose a batch...</option>
                        {filteredBatches.map(b => (
                          <option key={b._id} value={b._id}>
                            {b.name}{b.course?.name ? ` — ${b.course.name}` : ""}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </div>
                )}

                {/* Student Picker */}
                {audienceType === "specific_students" && (
                  <div className="wa-bc-filter-section">
                    <Form.Label>Search &amp; Select Students</Form.Label>
                    <div className="wa-bc-student-search-box">
                      <Search size={15} className="wa-bc-student-search-icon" />
                      <input
                        type="text"
                        className="wa-bc-student-search-input"
                        placeholder="Search by name or mobile number..."
                        value={studentQuery}
                        onChange={e => handleStudentSearch(e.target.value)}
                      />
                      {studentSearching && <Spinner animation="border" size="sm" />}
                    </div>

                    {studentResults.length > 0 && (
                      <div className="wa-bc-student-results">
                        {studentResults.filter(s => !selectedStudents.find(x => x._id === s._id)).map(s => (
                          <div key={s._id} className="wa-bc-student-result" onClick={() => addStudent(s)} role="button" tabIndex={0} onKeyDown={e => e.key === "Enter" && addStudent(s)}>
                            <User size={14} />
                            <span className="wa-bc-student-result__name">{s.name}</span>
                            <span className="wa-bc-student-result__meta">{s.mobile}</span>
                            {s.course?.name && <span className="wa-bc-student-result__meta">{s.course.name}</span>}
                          </div>
                        ))}
                      </div>
                    )}

                    {selectedStudents.length > 0 && (
                      <div className="wa-bc-selected-students">
                        <div className="wa-bc-selected-students__label">
                          Selected ({selectedStudents.length})
                          <button className="wa-bc-clear-all" onClick={() => setSelectedStudents([])}>Clear all</button>
                        </div>
                        <div className="wa-bc-chips">
                          {selectedStudents.map(s => (
                            <div key={s._id} className="wa-bc-chip">
                              <User size={12} />
                              <span>{s.name}</span>
                              <button className="wa-bc-chip__remove" onClick={() => removeStudent(s._id)} title="Remove"><X size={12} /></button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* External Numbers */}
                {audienceType === "external_numbers" && (
                  <div className="wa-bc-filter-section">
                    <label className="wa-form-label" style={{ marginBottom: 6 }}>Phone Numbers</label>
                    <textarea
                      className="wa-form-input wa-form-textarea"
                      rows={4}
                      value={externalNumbersText}
                      onChange={e => setExternalNumbersText(e.target.value)}
                      placeholder={"Enter phone numbers (one per line)\n919876543210\n918765432109"}
                    />
                    <div className="wa-form-hint" style={{ marginTop: 6 }}>
                      Include country code (e.g. 91 for India).
                    </div>
                    {parsedExternalNumbers.length > 0 && (
                      <div className="wa-form-hint" style={{ marginTop: 4 }}>
                        <strong>{parsedExternalNumbers.length}</strong> valid number{parsedExternalNumbers.length !== 1 ? "s" : ""} detected
                      </div>
                    )}
                  </div>
                )}

                {/* Recipient Count */}
                {audienceReady && (
                  <div className="wa-bc-recipient-count">
                    <Users size={14} />
                    {countLoading ? (
                      <span>Counting recipients…</span>
                    ) : recipientCount !== null ? (
                      <span>
                        <strong>{recipientCount.toLocaleString()}</strong> recipient
                        {recipientCount !== 1 ? "s" : ""} will receive this message
                      </span>
                    ) : (
                      <span>Unable to estimate recipients</span>
                    )}
                  </div>
                )}

                {/* Capacity — how long this actually takes, and whether the
                    daily cap will swallow most of it. Shown here rather than in
                    the confirm dialog so the operator can still change the
                    audience in response to it. */}
                {capacity && recipientCount > 0 && (
                  <>
                    {capacity.estimatedMinutes > 5 && (
                      <div className="wa-alert wa-alert--info" style={{ marginTop: 8 }}>
                        <Clock size={14} />
                        WhatsApp is rate-limited, so this sends over roughly{" "}
                        <strong>{formatDuration(capacity.estimatedMinutes)}</strong> in the
                        background. You can carry on using the LMS — you&rsquo;ll get a
                        notification when it finishes.
                      </div>
                    )}
                    {capacity.willExceedDailyLimit && (
                      <div className="wa-alert wa-alert--warning" style={{ marginTop: 8 }}>
                        <AlertTriangle size={14} />
                        Your daily WhatsApp limit is <strong>{capacity.dailyLimit.toLocaleString()}</strong>{" "}
                        messages and <strong>{capacity.dailyUsed.toLocaleString()}</strong> have been used
                        today, so about <strong>{capacity.overflowCount.toLocaleString()}</strong> of these
                        will be skipped rather than sent. Raise the limit in the{" "}
                        <strong>Config</strong> tab first if you need all of them to go out.
                      </div>
                    )}
                  </>
                )}
              </AppCardContent>
            </AppCard>

            {/* ── Step 3: Template Selection & Variables ── */}
            <AppCard>
              <AppCardHeader>
                <AppCardTitle><FileText size={18} /> Step 3 — Select Template &amp; Fill Variables</AppCardTitle>
                <p className="settings-card-desc">Choose an approved WhatsApp template</p>
              </AppCardHeader>
              <AppCardContent>
                {templates.length === 0 ? (
                  <EmptyState
                    icon={<FileText size={32} />}
                    title="No Approved Templates"
                    description='Sync templates from Meta in the Templates tab first.'
                  />
                ) : (
                  <>
                    <Form.Group className="mb-3">
                      <Form.Label>Template <span className="wa-required">*</span></Form.Label>
                      <Form.Select value={templateName} onChange={e => setTemplateName(e.target.value)}>
                        <option value="">Select a template...</option>
                        {templates.map(t => (
                          <option key={t._id} value={t.name}>
                            {t.name} ({t.language}) — {t.category || "MARKETING"}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>

                    {/* Variable inputs */}
                    {selectedTemplate && totalParams > 0 && (
                      <div className="wa-bc-vars">
                        <div className="wa-bc-vars__title">Template Variables ({totalParams})</div>
                        <div className="wa-sched-form__var-hint">
                          For each variable, choose <strong>Dynamic</strong> (per student) or <strong>Static</strong> (same for everyone).
                        </div>
                        {staleVars.length > 0 && (
                          <div className="wa-alert wa-alert--warning" style={{ marginBottom: 8 }}>
                            <AlertTriangle size={14} />
                            {staleVars.map((v) => dynamicVarLabel(v)).join(", ")}
                            {staleVars.length === 1 ? " is not available" : " are not available"} for this
                            audience — pick a different field, or the message will send with a gap there.
                          </div>
                        )}
                        {templateVars.header.length > 0 && (
                          <VarGroup label="Header" params={templateVars.header} prefix="header" paramModes={paramModes} paramValues={paramValues} setParamModes={setParamModes} setParamValues={setParamValues} audienceType={audienceType} />
                        )}
                        {templateVars.body.length > 0 && (
                          <VarGroup label="Body" params={templateVars.body} prefix="body" paramModes={paramModes} paramValues={paramValues} setParamModes={setParamModes} setParamValues={setParamValues} audienceType={audienceType} />
                        )}
                        {templateVars.button.length > 0 && (
                          <div className="wa-bc-vars__group">
                            <div className="wa-bc-vars__group-label">Buttons</div>
                            {templateVars.button.map(p => {
                              const k = `button_${p.key}`;
                              return (
                                <Form.Group key={k} className="mb-2">
                                  <Form.Label className="wa-bc-var-label">{p.label}</Form.Label>
                                  {p.baseUrl && (
                                    <div className="wa-sched-form__base-url">
                                      Base URL: <strong>{p.baseUrl}</strong>/<span className="wa-sched-form__suffix">{"{{suffix}}"}</span>
                                    </div>
                                  )}
                                  <Form.Control
                                    size="sm"
                                    value={paramValues[k] || ""}
                                    onChange={e => setParamValues(pv => ({ ...pv, [k]: e.target.value }))}
                                    placeholder={p.example || "Enter suffix..."}
                                  />
                                </Form.Group>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Live Preview */}
                    {selectedTemplate && (
                      <TemplatePreview template={selectedTemplate} vars={templateVars} values={paramValues} modes={paramModes} />
                    )}
                  </>
                )}
              </AppCardContent>
            </AppCard>
          </div>
        </Modal.Body>
        <Modal.Footer className="wa-sched-form__footer">
          <AppButton variant="outline" onClick={() => onClose(false)} disabled={saving}>Cancel</AppButton>
          <AppButton onClick={() => setShowConfirmModal(true)} disabled={!canSubmit || saving}>
            <CalendarClock size={14} /> {editingMessage ? "Update Schedule" : "Schedule Message"}
          </AppButton>
        </Modal.Footer>
      </Modal>

      {/* Confirm */}
      <ConfirmModal
        show={showConfirmModal}
        onHide={() => { if (!saving) setShowConfirmModal(false); }}
        onConfirm={handleSubmit}
        // Stacked on top of the form's Bootstrap modal (z-index 1055) — without
        // this it renders behind it and the schedule can never be confirmed.
        overlayClassName="wa-sched-confirm-overlay"
        title={editingMessage ? "Confirm Update" : "Confirm Schedule"}
        confirmText={editingMessage ? "Update" : "Schedule"}
        cancelText="Cancel"
        loading={saving}
        message={
          <div className="wa-sched-confirm">
            <div className="wa-bc-summary">
              <div className="wa-bc-summary__row">
                <span className="wa-bc-summary__label">Title</span>
                <span>{title}</span>
              </div>
              <div className="wa-bc-summary__row">
                <span className="wa-bc-summary__label">{isRepeating ? "First send" : "When"}</span>
                <span>
                  {preview?.occurrences?.[0]
                    ? moment(preview.occurrences[0]).format("MMM DD, YYYY · hh:mm A")
                    : scheduledDate && scheduledTime
                      ? moment(`${scheduledDate}T${scheduledTime}`).format("MMM DD, YYYY · hh:mm A")
                      : "—"}
                </span>
              </div>
              {isRepeating && (
                <div className="wa-bc-summary__row">
                  <span className="wa-bc-summary__label">Repeats</span>
                  <span>{preview?.description || "—"}</span>
                </div>
              )}
              <div className="wa-bc-summary__row">
                <span className="wa-bc-summary__label">Audience</span>
                <span>{AUDIENCE_TYPES.find(a => a.value === audienceType)?.label || audienceType}</span>
              </div>
              <div className="wa-bc-summary__row">
                <span className="wa-bc-summary__label">Recipients</span>
                <span>{recipientCount?.toLocaleString() ?? "?"}</span>
              </div>
              <div className="wa-bc-summary__row">
                <span className="wa-bc-summary__label">Template</span>
                <span>{templateName}</span>
              </div>
              {capacity?.estimatedMinutes > 5 && (
                <div className="wa-bc-summary__row">
                  <span className="wa-bc-summary__label">Takes about</span>
                  <span>{formatDuration(capacity.estimatedMinutes)}</span>
                </div>
              )}
            </div>
            {!editingMessage && (
              <div className="wa-alert wa-alert--info" style={{ marginTop: 10 }}>
                <CalendarClock size={14} />
                Sent automatically at the scheduled time, in the background — you&rsquo;ll get a
                notification when it finishes.
              </div>
            )}
            {/* Recipients are per occurrence, and a repeat multiplies them.
                Said plainly here because the daily-limit warning above is sized
                for a single send and would otherwise read as the whole cost. */}
            {isRepeating && recipientCount > 0 && (
              <div className="wa-alert wa-alert--warning" style={{ marginTop: 8 }}>
                <Repeat size={14} />
                This sends <strong>{recipientCount.toLocaleString()}</strong> message
                {recipientCount !== 1 ? "s" : ""} <strong>every time it repeats</strong>
                {recurrence.endMode === "after_count" && (
                  <> — about {(recipientCount * Number(recurrence.maxOccurrences || 1)).toLocaleString()} in total</>
                )}
                {recurrence.endMode === "never" && <> , with no end date set</>}.
                Each occurrence counts against your daily WhatsApp limit on the day it runs.
              </div>
            )}
            {capacity?.willExceedDailyLimit && (
              <div className="wa-alert wa-alert--warning" style={{ marginTop: 8 }}>
                <AlertTriangle size={14} />
                About {capacity.overflowCount.toLocaleString()} of these will be skipped —
                your daily limit is {capacity.dailyLimit.toLocaleString()}.
              </div>
            )}
          </div>
        }
      />
    </>
  );
};

/* ══════════════════════════════════════════════════════════════════════════════
   Repeat picker
   ══════════════════════════════════════════════════════════════════════════════ */
const RepeatPicker = ({ recurrence, setRec, audienceType, batchName }) => {
  const isRepeating = recurrence.frequency !== "none";
  const classAnchored = recurrence.anchor === "before_class";
  const canAnchorToClass = audienceType === "batch";

  const toggleDay = (d) => setRec({
    daysOfWeek: recurrence.daysOfWeek.includes(d)
      ? recurrence.daysOfWeek.filter((x) => x !== d)
      : [...recurrence.daysOfWeek, d].sort((a, b) => a - b),
  });

  const unitLabel = { daily: "day", weekly: "week", monthly: "month" }[recurrence.frequency] || "";

  return (
    <div className="wa-sched-repeat">
      <Form.Label className="wa-sched-repeat__label"><Repeat size={14} /> Repeat</Form.Label>

      <div className="wa-sched-repeat__freq">
        {FREQUENCY_OPTIONS.map((f) => (
          <button
            key={f.value}
            type="button"
            className={`wa-sched-repeat__freq-btn ${recurrence.frequency === f.value ? "is-active" : ""}`}
            onClick={() => setRec({
              frequency: f.value,
              // Weekday selection is meaningless outside a weekly rule, and
              // leaving a stale set behind makes the summary lie.
              daysOfWeek: f.value === "weekly" ? recurrence.daysOfWeek : [],
              dayOfMonth: f.value === "monthly" ? (recurrence.dayOfMonth || new Date().getDate()) : null,
              anchor: f.value === "none" ? "fixed" : recurrence.anchor,
              interval: 1,
            })}
          >
            <f.icon size={14} /> {f.label}
          </button>
        ))}
      </div>

      {isRepeating && (
        <div className="wa-sched-repeat__body">
          {/* ── Every N ── */}
          <div className="wa-sched-repeat__row">
            <span className="wa-sched-repeat__row-label">Every</span>
            <Form.Control
              type="number"
              size="sm"
              min={1}
              max={recurrence.frequency === "weekly" ? 52 : 31}
              className="wa-sched-repeat__interval"
              value={recurrence.interval}
              onChange={(e) => setRec({ interval: Math.max(1, Number(e.target.value) || 1) })}
            />
            <span className="wa-sched-repeat__row-label">
              {unitLabel}{Number(recurrence.interval) !== 1 ? "s" : ""}
            </span>
          </div>

          {/* ── Weekday chips ── */}
          {recurrence.frequency === "weekly" && (
            <div className="wa-sched-repeat__row wa-sched-repeat__row--wrap">
              <span className="wa-sched-repeat__row-label">On</span>
              <div className="wa-sched-repeat__days">
                {WEEKDAYS.map((d) => (
                  <button
                    key={d.value}
                    type="button"
                    title={d.label}
                    aria-label={d.label}
                    aria-pressed={recurrence.daysOfWeek.includes(d.value)}
                    className={`wa-sched-repeat__day ${recurrence.daysOfWeek.includes(d.value) ? "is-on" : ""}`}
                    onClick={() => toggleDay(d.value)}
                  >
                    {d.short}
                  </button>
                ))}
              </div>
              {recurrence.daysOfWeek.length === 0 && (
                <span className="wa-sched-repeat__hint-error">Pick at least one day</span>
              )}
            </div>
          )}

          {/* ── Day of month ── */}
          {recurrence.frequency === "monthly" && (
            <div className="wa-sched-repeat__row">
              <span className="wa-sched-repeat__row-label">On day</span>
              <Form.Control
                type="number"
                size="sm"
                min={1}
                max={31}
                className="wa-sched-repeat__interval"
                value={recurrence.dayOfMonth || ""}
                onChange={(e) => setRec({ dayOfMonth: Math.min(31, Math.max(1, Number(e.target.value) || 1)) })}
              />
              {Number(recurrence.dayOfMonth) > 28 && (
                <span className="wa-sched-repeat__hint">
                  Months without a {recurrence.dayOfMonth}th are skipped, not moved to the last day.
                </span>
              )}
            </div>
          )}

          {/* ── Timing anchor ── */}
          <div className="wa-sched-repeat__row wa-sched-repeat__row--wrap">
            <span className="wa-sched-repeat__row-label">Send</span>
            <div className="wa-sched-form__status-btns">
              <button
                type="button"
                className={`wa-mode-btn ${!classAnchored ? "wa-mode-btn--active" : ""}`}
                onClick={() => setRec({ anchor: "fixed" })}
              >
                <Clock size={13} /> At a fixed time
              </button>
              <button
                type="button"
                className={`wa-mode-btn ${classAnchored ? "wa-mode-btn--active" : ""}`}
                disabled={!canAnchorToClass}
                title={canAnchorToClass ? "" : "Pick the Batch audience in Step 2 to use the batch's class time"}
                onClick={() => canAnchorToClass && setRec({ anchor: "before_class" })}
              >
                <Timer size={13} /> Before class starts
              </button>
            </div>
          </div>

          {classAnchored ? (
            <>
              <div className="wa-sched-repeat__row wa-sched-repeat__row--wrap">
                <span className="wa-sched-repeat__row-label">Lead time</span>
                <div className="wa-sched-repeat__offsets">
                  {CLASS_OFFSETS.map((m) => (
                    <button
                      key={m}
                      type="button"
                      className={`wa-sched-repeat__offset ${Number(recurrence.offsetMinutes) === m ? "is-on" : ""}`}
                      onClick={() => setRec({ offsetMinutes: m })}
                    >
                      {m < 60 ? `${m} min` : "1 hour"}
                    </button>
                  ))}
                </div>
              </div>
              <div className="wa-alert wa-alert--info">
                <Timer size={14} />
                {batchName ? <>Sends before every class of <strong>{batchName}</strong>, </> : "Sends before every class of the batch you pick in Step 2, "}
                using the batch&rsquo;s own start time and meeting days. Institute holidays are
                skipped automatically, so the day the batch does not meet gets no message.
              </div>
            </>
          ) : null}

          {/* ── End condition ── */}
          <div className="wa-sched-repeat__row wa-sched-repeat__row--wrap">
            <span className="wa-sched-repeat__row-label">Ends</span>
            <div className="wa-sched-form__status-btns">
              {[
                { value: "never", label: "Never" },
                { value: "on_date", label: "On a date" },
                { value: "after_count", label: "After N sends" },
              ].map((o) => (
                <button
                  key={o.value}
                  type="button"
                  className={`wa-mode-btn ${recurrence.endMode === o.value ? "wa-mode-btn--active" : ""}`}
                  onClick={() => setRec({ endMode: o.value })}
                >
                  {o.label}
                </button>
              ))}
            </div>
            {recurrence.endMode === "on_date" && (
              <Form.Control
                type="date"
                size="sm"
                className="wa-sched-repeat__enddate"
                value={recurrence.endDate}
                min={moment().format("YYYY-MM-DD")}
                onChange={(e) => setRec({ endDate: e.target.value })}
              />
            )}
            {recurrence.endMode === "after_count" && (
              <Form.Control
                type="number"
                size="sm"
                min={1}
                max={1000}
                className="wa-sched-repeat__interval"
                value={recurrence.maxOccurrences}
                onChange={(e) => setRec({ maxOccurrences: Math.max(1, Number(e.target.value) || 1) })}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/* ── Occurrence preview ─────────────────────────────────────────────────────
 *
 * Every date here is computed by the backend, by the same functions the cron
 * calls. Nothing about "when does this fire" is worked out in the browser.
 */
const OccurrencePreview = ({ preview, loading, ready, classAnchored }) => {
  if (!ready) {
    return (
      <div className="wa-sched-occ wa-sched-occ--muted">
        <CalendarDays size={14} /> Finish the repeat settings to see when this sends.
      </div>
    );
  }
  if (loading || !preview) {
    return (
      <div className="wa-sched-occ wa-sched-occ--muted">
        <Loader2 size={14} className="wa-spin" /> Working out the send dates…
      </div>
    );
  }
  if (preview.warning && preview.occurrences.length === 0) {
    return (
      <div className="wa-alert wa-alert--warning">
        <AlertTriangle size={14} /> {preview.warning}
      </div>
    );
  }

  return (
    <div className="wa-sched-occ">
      <div className="wa-sched-occ__head">
        <Repeat size={14} />
        <strong>{preview.description}</strong>
      </div>
      <ol className="wa-sched-occ__list">
        {preview.occurrences.map((iso, i) => (
          <li key={iso}>
            <span className="wa-sched-occ__n">{i === 0 ? "Next" : `#${i + 1}`}</span>
            <span className="wa-sched-occ__when">
              {moment(iso).format("ddd, DD MMM YYYY · hh:mm A")}
            </span>
            <span className="wa-sched-occ__rel">{moment(iso).fromNow()}</span>
          </li>
        ))}
      </ol>
      <div className="wa-form-hint">
        {classAnchored
          ? "Recomputed from the batch's timetable before every send, so a change to the class time is picked up automatically."
          : "The next send is armed as soon as one goes out, so the series keeps running unattended."}
      </div>
    </div>
  );
};

/* ── Variable Group ────────────────────────────────────────────────────────── */
const VarGroup = ({ label, params, prefix, paramModes, paramValues, setParamModes, setParamValues, audienceType }) => (
  <div className="wa-bc-vars__group">
    <div className="wa-bc-vars__group-label">{label}</div>
    {params.map(p => {
      const k = `${prefix}_${p.key}`;
      const mode = paramModes[k] || "static";
      return (
        <div key={k} className="mb-2">
          <Form.Label className="wa-bc-var-label">{p.label}</Form.Label>
          <div className="wa-sched-form__var-row">
            <div className="wa-sched-form__var-btns">
              <button type="button" className={`wa-mode-btn ${mode === "dynamic" ? "wa-mode-btn--active" : ""}`} onClick={() => { setParamModes(m => ({ ...m, [k]: "dynamic" })); setParamValues(pv => ({ ...pv, [k]: "" })); }}>Dynamic</button>
              <button type="button" className={`wa-mode-btn ${mode === "static" ? "wa-mode-btn--active" : ""}`} onClick={() => { setParamModes(m => ({ ...m, [k]: "static" })); setParamValues(pv => ({ ...pv, [k]: "" })); }}>Static</button>
            </div>
            {mode === "dynamic" ? (
              <Form.Select size="sm" className="wa-sched-form__var-select" value={paramValues[k] || ""} onChange={e => setParamValues(pv => ({ ...pv, [k]: e.target.value }))}>
                <option value="">-- Select variable --</option>
                {renderVarOptions(audienceType)}
              </Form.Select>
            ) : (
              <Form.Control size="sm" value={paramValues[k] || ""} onChange={e => setParamValues(pv => ({ ...pv, [k]: e.target.value }))} placeholder={p.example || `Enter ${p.label}...`} />
            )}
          </div>
        </div>
      );
    })}
  </div>
);

/* ── Template Preview ──────────────────────────────────────────────────────── */
const TemplatePreview = ({ template, vars, values, modes = {} }) => {
  const hc = template.components?.find(c => c.type === "HEADER");
  const bc = template.components?.find(c => c.type === "BODY");
  const fc = template.components?.find(c => c.type === "FOOTER");
  const btc = template.components?.find(c => c.type === "BUTTONS");

  const previewValues = { ...values };
  Object.entries(modes).forEach(([k, mode]) => {
    if (mode === "dynamic" && values[k]) {
      previewValues[k] = `[${dynamicVarLabel(values[k])}]`;
    }
  });

  const headerText = hc?.format === "TEXT" ? _fillPreview(hc.text, vars.header, previewValues, "header") : null;
  const bodyText = bc?.text ? _fillPreview(bc.text, vars.body, previewValues, "body") : null;
  const footerText = fc?.text || null;

  return (
    <div className="wa-bc-preview">
      <div className="wa-bc-preview__label">Preview</div>
      <div className="wa-bc-preview__bubble">
        {headerText && <div className="wa-bc-preview__header">{headerText}</div>}
        {bodyText && <div className="wa-bc-preview__body">{bodyText}</div>}
        {footerText && <div className="wa-bc-preview__footer">{footerText}</div>}
        {btc?.buttons?.length > 0 && (
          <div className="wa-bc-preview__buttons">
            {btc.buttons.map((btn, i) => <div key={btn.text || i} className="wa-bc-preview__btn">{btn.text}</div>)}
          </div>
        )}
      </div>
    </div>
  );
};

export default ScheduledMessagesTab;
